import L, { GridLayer } from "leaflet";
import { unbindLayerTooltip } from "./tooltipLifecycle";
import "leaflet-geometryutil";
import area from "@turf/area";
import length from "@turf/length";
import pointOnFeature from "@turf/point-on-feature";
import polylabel from "polylabel";
import { BBox, Feature, LineString, MultiLineString, MultiPolygon, Polygon } from "geojson";
import { GeoLocationAddress } from "types/geoLocation";
import { getGeolocation } from "services/geolocation/getGeolocation";
import { SPECIAL_STYLE_CATEGORIES, SPECIAL_STYLE_CATEGORIES_TYPE } from "config/config-general";
import { lightenDarkenColor } from "utils/colorModification";
import BoxPattern from "./Extensions/patterns/BoxPattern";
import DashedStripePattern from "./Extensions/patterns/DashedStripePattern";
import { createCustomCategoryPattern } from "./Extensions/patterns/predefinedPatterns";
import { geoJsonGetBbox } from "lib/turf/geoJsonHelperFunctions";
import { LatLngProps } from "types/firestoreFeatures";
import { calculateDistance, getPointBoundingBox } from "utils/geoTools";
import { drawHelpCircleOptions, PX_CORRECTION } from "features/map/config/leafletConfig";
import { isValidNumber } from "utils/numberModification";
import { CustomCategoryStyle } from "types/auth";
import {
  AddCirclesToLayerProps,
  MapPaneProps,
  RemoveFeatureFromLayerProps,
} from "features/map/types/map";
import { pointInsidePolygon } from "lib/turf/geoJsonHelperFunctions";

declare global {
  interface Window {
    __leafletTilePatchApplied?: boolean;
  }
}

// Parameters
const DEFAULT_POINT_ZOOM = 17;
const MAP_NAVIGATION_DURATION = 0.8;
const VISIBLE_TARGET_CENTER_MARGIN = 0.1;
const ZOOM_TO_POINT_BUFFER = 0; //0.01;
const POLYGON_SELECTED_BUFFER_ARRAY: [number, number, number, number] = [0, -0.002, 0, 0.001];
const POINT_SELECTED_BUFFER_ARRAY: [number, number, number, number] = [0, -0.0012, 0, 0.001];

// Types
export type LayerRole = "geometry" | "label" | "buffer";
type ZoomToBboxOffset =
  | BBox
  | {
      type: "ratio";
      value: number;
    };

/**
 * Returns whether a Leaflet map still owns a live map container.
 *
 * Async layer creation may complete after the map page has unmounted. Leaflet
 * cannot safely add a layer to a map that has already been removed.
 */
export function isActiveLeafletMap(map: L.Map): boolean {
  const mapInstance = map as any;
  const panes = mapInstance._panes;

  return Boolean(
    mapInstance._container &&
      mapInstance._containerId &&
      mapInstance._container._leaflet_id === mapInstance._containerId &&
      mapInstance._loaded &&
      panes?.mapPane?.isConnected &&
      panes?.markerPane?.isConnected,
  );
}

/** Adds a layer only while its map still has the panes required by Leaflet. */
export function addLayerToActiveLeafletMap(map: L.Map | null | undefined, layer: L.Layer): boolean {
  if (!map || !isActiveLeafletMap(map)) return false;

  map.addLayer(layer);
  return true;
}

export function getAreaInHa(polygon: L.Polygon) {
  const geoPoly = polygon.toGeoJSON();
  return Math.round((area(geoPoly) / 10000) * 100) / 100;
}

export function getFeatureAreaInHa(feature: Polygon | MultiPolygon) {
  return Math.round((area(feature) / 10000) * 100) / 100;
}

export function getLineLengthInMeters(line: L.Polyline) {
  const geoLine = line.toGeoJSON();
  return length(geoLine) * 1000;
}

export function getFeatureLineLengthInMeters(feature: Feature<LineString | MultiLineString>) {
  return length(feature) * 1000;
}

export function getPointInsideLeafletPolygon(polygon: L.Polygon) {
  const geoPoint = pointOnFeature(polygon.toGeoJSON());
  return { lat: geoPoint.geometry.coordinates[1], lng: geoPoint.geometry.coordinates[0] };
}

export function getPointInsideGeoJsonPolygon(polygon: Polygon) {
  try {
    const labelCoords = polylabel(polygon.coordinates.slice(0, 4), 0.5);
    if (!pointInsidePolygon(labelCoords, polygon.coordinates)) {
      throw new Error("Point not inside polygon");
    }
    return { lat: labelCoords[1], lng: labelCoords[0] };
  } catch {
    const geoPoint = pointOnFeature(polygon);
    return { lat: geoPoint.geometry.coordinates[1], lng: geoPoint.geometry.coordinates[0] };
  }
}

export function removeFeatureFromLayer({ layer, idType, featureId }: RemoveFeatureFromLayerProps) {
  let removedLayer: null | L.Layer = null;
  // @ts-ignore
  Object.values(layer._layers).forEach((lay: any) => {
    if (lay.options[idType] === featureId) {
      removedLayer = { ...lay };
      // @ts-ignore
      layer.removeLayer(lay);
    }
  });
  return removedLayer;
}

export function addCircleToLayer({ layer, radius, location, vertexId }: AddCirclesToLayerProps) {
  // @ts-ignore
  const circ = L.circle(location, { ...drawHelpCircleOptions, radius, vertexId });
  circ.addTo(layer);
  return circ;
}

export function updateRadiusOfCirclesInLayer(layer: L.LayerGroup | L.FeatureGroup, radius: number) {
  // @ts-ignore
  Object.values(layer._layers).forEach((lay: L.Circle) => lay.setRadius(radius));
}

export function resetLayer(layer: L.LayerGroup | L.FeatureGroup) {
  layer.clearLayers();
}

export function createPaneList(map: L.Map, paneList: MapPaneProps[]): void {
  paneList.forEach((el) => {
    map.createPane(el.label);
    // @ts-ignore
    map.getPane(el.label).style.zIndex = el.zIndex;
  });
}

export function getPointBbox(point: LatLngProps, distanceM: number = 10, map?: L.Map): BBox {
  const bbox = getPointBoundingBox(point.lat, point.lng, distanceM);
  if (map) {
    // Insert bbox polygon
    L.polygon([
      [bbox[1], bbox[0]],
      [bbox[1], bbox[2]],
      [bbox[3], bbox[2]],
      [bbox[3], bbox[0]],
    ]).addTo(map);
  }
  return bbox as BBox;
}

export function resetWMSLayers(map: L.Map) {
  // @ts-ignore
  for (const l of Object.values(map._layers)) {
    // @ts-ignore
    if (l.wmsParams) {
      // @ts-ignore
      map.removeLayer(l);
    }
  }
}

export function getWMSLayerFromId(map: L.Map, id: string) {
  // @ts-ignore
  for (const l of Object.values(map._layers)) {
    // @ts-ignore
    if (l.wmsParams && L.options && L.options.id === id) {
      // @ts-ignore
      return l;
    }
  }
  return null;
}

export function checkIfLayerIsActiveFromOptionsId(map: L.Map, id: string): any | null {
  // @ts-ignore
  for (const l of Object.values(map._layers)) {
    // @ts-ignore
    if (l.options && l.options.id && l.options.id === id) return l;
  }
  return null;
}

export function checkIfMarkerInserted(map: L.Map, id: string): L.Marker | null {
  // @ts-ignore
  for (const l of Object.values(map._layers)) {
    // @ts-ignore
    if (l.options.id === id) return l;
  }
  return null;
}

export function findLayerById(
  map: L.Map,
  id: string,
  opts?: { roles?: LayerRole[]; layerGroups?: string[] },
): L.Layer | null {
  const roles = new Set(opts?.roles ?? ["geometry"]); // default: geometry only
  const lg = opts?.layerGroups ? new Set(opts.layerGroups) : null;

  // @ts-ignore – access to _layers
  for (const l of Object.values<L.Layer>(map._layers)) {
    const opt = (l as any).options || {};
    if (opt?.id !== id) continue;
    if (!roles.has(opt?.role)) continue;
    if (lg && !lg.has(opt?.layerGroupName)) continue;
    return l;
  }
  return null;
}

export function existsInAnotherLayerGroup(
  map: L.Map,
  id: string,
  currentLayerGroupName: string,
  roles: LayerRole[] = ["geometry"],
): boolean {
  // @ts-ignore
  for (const l of Object.values<L.Layer>(map._layers)) {
    const opt = (l as any).options || {};
    if (opt?.id !== id) continue;
    if (!roles.includes(opt?.role)) continue;
    if (opt?.layerGroupName && opt.layerGroupName !== currentLayerGroupName) return true;
  }
  return false;
}

export const checkForGlobalEditMode = (map: L.Map): { enabled: boolean; modes: string[] } => {
  let enabled = false;
  const modes: string[] = [];
  if (map.pm.globalEditModeEnabled()) {
    enabled = true;
    modes.push("edit");
  }
  if (map.pm.globalDragModeEnabled()) {
    enabled = true;
    modes.push("drag");
  }
  if (map.pm.globalRemovalModeEnabled()) {
    enabled = true;
    modes.push("remove");
  }
  if (map.pm.globalCutModeEnabled()) {
    enabled = true;
    modes.push("cut");
  }
  if (map.pm.globalRotateModeEnabled()) {
    enabled = true;
    modes.push("rotate");
  }
  if (map.pm.globalDrawModeEnabled()) {
    enabled = true;
    modes.push("draw");
  }

  return { enabled, modes };
};

export function findFeatureFromId(
  layer: L.FeatureGroup | L.GeoJSON,
  featureId: string,
): L.Layer | null {
  layer.eachLayer((lay) => {
    //@ts-ignore
    if (lay.feature) {
      //@ts-ignore
      if (lay.feature.properties.id === featureId) {
        return lay;
      }
    } else {
      //@ts-ignore
      //@ts-ignore
      if (lay.options.id === featureId) {
        return lay;
      }
    }
  });
  return null;
}

export function removeFeatureFromLayerGroup(
  layer: L.FeatureGroup | L.GeoJSON,
  featureId: string,
): boolean {
  const layers = layer.getLayers(); // array copy, safe to mutate original group
  let removed = false;

  for (const lay of layers) {
    const id =
      // @ts-ignore
      lay.feature?.properties?.id ??
      // @ts-ignore
      lay.options?.id;

    if (id === featureId) {
      layer.removeLayer(lay);
      removed = true;
    }
  }

  // A single logical GeoJSON feature can be represented by several Leaflet
  // children (for example after a polygon has been split into a
  // MultiPolygon). Remove all of them so a subsequent addData/update is a
  // true replacement rather than a temporary duplicate.
  return removed;
}

export function bindToolTipToLayer(
  layer: L.FeatureGroup | L.GeoJSON | L.Layer,
  text: string,
  options?: { pane?: string },
) {
  // Set options
  const pane = options?.pane || "toolTipPane";
  try {
    // @ts-ignore
    if (!layer._layers) {
      layer.bindTooltip(text, { pane: pane, sticky: true });
      return;
    }
    // @ts-ignore
    Object.values(layer._layers).forEach((lay: L.Layer) => {
      lay.bindTooltip(text, { pane: pane, sticky: true });
    });
  } catch (error) {
    console.warn("Not able to bind tooltip to layer!", { error, layer, text, options });
  }
}

export function unbindTooltipToLayer(layer: L.FeatureGroup | L.GeoJSON) {
  try {
    // @ts-ignore
    Object.values(layer._layers).forEach((lay: L.Layer) => {
      // @ts-ignore
      unbindLayerTooltip(lay);
    });
  } catch (error) {
    console.warn("Not able to unbindTooltip!", { error, layer });
  }
}

export function zoomToBbox(
  map: L.Map,
  bbox: BBox | BBox[],
  offset?: ZoomToBboxOffset,
  animateOptions?: L.FitBoundsOptions,
) {
  if (!map) {
    console.warn("Error in zoomToBbox - no map object");
    return;
  }

  try {
    const merged = mergeBboxes(bbox);
    if (!merged) return;

    const normalizedMerged = normalizeBbox(merged);

    const resolvedOffset: BBox | undefined = Array.isArray(offset)
      ? offset
      : offset?.type === "ratio"
      ? getBboxOffsetByRatio(normalizedMerged, offset.value)
      : undefined;

    const withOffset: BBox = resolvedOffset
      ? [
          normalizedMerged[0] + resolvedOffset[0],
          normalizedMerged[1] + resolvedOffset[1],
          normalizedMerged[2] + resolvedOffset[2],
          normalizedMerged[3] + resolvedOffset[3],
        ]
      : normalizedMerged;

    const bboxAll = normalizeBbox(withOffset);
    let [lng1, lat1, lng2, lat2] = bboxAll;

    const options: L.FitBoundsOptions = {
      // Keep the journey legible without making navigation wait for a long flight.
      animate: true,
      duration: MAP_NAVIGATION_DURATION,
      ...animateOptions,
    };

    const mapSize = map.getSize();
    const mapIsStable = (map as any)._loaded && mapSize.x > 0 && mapSize.y > 0;
    const shouldAnimate = options.animate !== false && mapIsStable;

    const isPointBbox = lat1 === lat2 && lng1 === lng2;
    if (isPointBbox) {
      const point = L.latLng(lat1, lng1);

      const targetZoom =
        typeof options.maxZoom === "number"
          ? options.maxZoom
          : Math.max(map.getZoom(), DEFAULT_POINT_ZOOM);

      if (mapIsStable && isTargetAlreadyInView(map, L.latLngBounds(point, point), targetZoom)) {
        return bboxAll;
      }
      map.stop();

      if (shouldAnimate) {
        map.flyTo(point, targetZoom, {
          animate: true,
          duration: options.duration,
          easeLinearity: options.easeLinearity,
          noMoveStart: options.noMoveStart,
        } as L.ZoomPanOptions);
      } else {
        map.setView(point, targetZoom, { animate: false });
      }

      return bboxAll;
    }

    if (lat1 === lat2) {
      lat1 -= ZOOM_TO_POINT_BUFFER;
      lat2 += ZOOM_TO_POINT_BUFFER;
    }

    if (lng1 === lng2) {
      lng1 -= ZOOM_TO_POINT_BUFFER;
      lng2 += ZOOM_TO_POINT_BUFFER;
    }

    const bounds = L.latLngBounds(L.latLng(lat1, lng1), L.latLng(lat2, lng2));

    if (!bounds.isValid()) {
      console.warn("Invalid bounds in zoomToBbox", { bbox, bboxAll, bounds });
      return;
    }

    const paddingTopLeft = L.point(options.paddingTopLeft || options.padding || [0, 0]);
    const paddingBottomRight = L.point(options.paddingBottomRight || options.padding || [0, 0]);
    const targetZoom = Math.min(
      typeof options.maxZoom === "number" ? options.maxZoom : Infinity,
      map.getBoundsZoom(bounds, false, paddingTopLeft.add(paddingBottomRight)),
    );
    if (mapIsStable && isTargetAlreadyInView(map, bounds, targetZoom)) {
      return bboxAll;
    }
    map.stop();

    if (shouldAnimate) {
      map.flyToBounds(bounds, options);
    } else {
      map.fitBounds(bounds, { ...options, animate: false });
    }

    return bboxAll;
  } catch (error) {
    console.error("Error in zoomToBbox", { bbox, error });
  }
}

function isTargetAlreadyInView(map: L.Map, bounds: L.LatLngBounds, targetZoom: number): boolean {
  if (map.getZoom() < targetZoom || !map.getBounds().contains(bounds)) return false;

  const size = map.getSize();
  const center = map.latLngToContainerPoint(bounds.getCenter());
  return (
    Math.abs(center.x - size.x / 2) <= size.x * VISIBLE_TARGET_CENTER_MARGIN &&
    Math.abs(center.y - size.y / 2) <= size.y * VISIBLE_TARGET_CENTER_MARGIN
  );
}

export function getBboxOffsetByRatio(bbox: BBox | BBox[], ratio = 0.1): BBox {
  const merged = mergeBboxes(bbox);
  if (!merged) return [0, 0, 0, 0];

  const [minLng, minLat, maxLng, maxLat] = normalizeBbox(merged);

  const lngSpan = Math.abs(maxLng - minLng);
  const latSpan = Math.abs(maxLat - minLat);

  const lngOffset = lngSpan * ratio;
  const latOffset = latSpan * ratio;

  return [-lngOffset, -latOffset, lngOffset, latOffset];
}

export function zoomToFeature(map: L.Map, feature: Feature, selected?: boolean) {
  if (!map) return;
  if (!feature || !feature.geometry || !feature.geometry.type) return;
  // Get buffer array based on type
  let bufferArray = POLYGON_SELECTED_BUFFER_ARRAY;
  if (feature.geometry.type === "Point") {
    bufferArray = POINT_SELECTED_BUFFER_ARRAY;
  }
  zoomToBbox(map, geoJsonGetBbox(feature), selected ? bufferArray : undefined);
}

export async function zoomToAddress(map: L.Map, address: GeoLocationAddress) {
  const res = await getGeolocation(address);
  if (res && res.bounds) {
    const c1 = L.latLng(res.bounds.northeast.lat, res.bounds.northeast.lng);
    const c2 = L.latLng(res.bounds.southwest.lat, res.bounds.southwest.lng);
    const b = L.latLngBounds(c1, c2);
    if (!b.isValid()) return;
    map.flyToBounds(b, { animate: true, duration: MAP_NAVIGATION_DURATION });
  } else if (res && res.location) {
    const c1 = L.latLng(
      res.location.lat + ZOOM_TO_POINT_BUFFER,
      res.location.lng + ZOOM_TO_POINT_BUFFER,
    );
    const c2 = L.latLng(
      res.location.lat - ZOOM_TO_POINT_BUFFER,
      res.location.lng - ZOOM_TO_POINT_BUFFER,
    );
    const b = L.latLngBounds(c1, c2);
    if (!b.isValid()) return;
    map.flyToBounds(b, { animate: true, duration: MAP_NAVIGATION_DURATION });
  }
}

export function getGeoJsonPointFromLocation(
  location: { lat: number; lng: number },
  properties?: any,
): Feature {
  const lng = location.lng;
  const lat = location.lat;
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
    properties: { ...properties },
  };
}

export function getGeoJsonPointFromBbox(bbox: BBox, properties?: any): Feature {
  // Get coordinates
  const lat = (bbox[1] + bbox[3]) / 2;
  const lng = (bbox[0] + bbox[2]) / 2;
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
    properties: { ...properties },
  };
}

export function getDistanceBetweenPoints(p1: LatLngProps, p2: LatLngProps, map: L.Map) {
  if (!p1 || !p2) return -1;
  const LP1 = L.latLng(p1.lat, p1.lng);
  const LP2 = L.latLng(p1.lat, p2.lng);

  return map.distance(LP1, LP2);
}

export function getDistanceTo(p1: LatLngProps, p2: LatLngProps) {
  if (!p1 || !p2) return -1;
  return calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng);
}

export function addToWithPurge(polygon: L.Polygon, map: L.Map) {
  polygon.addTo(map);
}

interface CreateNewPatttern {
  featureId: string;
  transparent?: boolean;
  color: string;
  category: string | SPECIAL_STYLE_CATEGORIES_TYPE;
  patternOverwrite?: CustomCategoryStyle;
}
export function createNewPattern({
  featureId,
  transparent,
  color,
  category,
  patternOverwrite,
}: CreateNewPatttern) {
  // If there is an overwrite pattern, use it. Else create default pattern based on category
  if (!patternOverwrite) {
    return createDefaultCategoryPattern({ featureId, transparent, color, category });
  }
  // Set custom pattern based on overwrite
  return createCustomCategoryPattern({
    patternType: patternOverwrite.patternType,
    id: featureId,
    category,
    transparent: !!transparent,
    color,
  });
}

function createDefaultCategoryPattern({
  featureId,
  transparent,
  color,
  category,
}: CreateNewPatttern) {
  // Return null if no special patterne
  if (!SPECIAL_STYLE_CATEGORIES.includes(category as SPECIAL_STYLE_CATEGORIES_TYPE)) return null;
  // Setup parameters
  const transp = transparent ? transparent : false;
  const spaceOpacity = transp ? 0 : 1;
  const outerFillOpacity = transp ? 0 : 1;
  const clr = transp ? "#7f7f7f" : lightenDarkenColor(color, -30);
  if (category === "intensive") {
    return BoxPattern({
      id: featureId,
      transparent: transp,
      type: "intensive",
      orgColor: color,
      innerWeight: 0,
      outerWeight: 0,
      innerFillColor: lightenDarkenColor(color, -30),
      outerFillColor: color,
      outerFillOpacity: outerFillOpacity,
      width: 24,
      height: 24,
    });
  } else if (category === "untouched") {
    return DashedStripePattern({
      id: featureId,
      transparent: transp,
      type: "untouched",
      orgColor: color,
      angle: 45,
      weight: 2,
      spaceWeight: 9,
      color: clr,
      spaceColor: color,
      opacity: 1,
      spaceOpacity: spaceOpacity,
    });
  } else if (category === "biodiversity") {
    return DashedStripePattern({
      id: featureId,
      transparent: transp,
      type: "biodiversity",
      orgColor: color,
      angle: 45,
      weight: 2,
      spaceWeight: 9,
      color: clr,
      spaceColor: color,
      opacity: 1,
      spaceOpacity: spaceOpacity,
      dashArray: "2,5",
    });
  }
  return null;
}

export function geoJsonCoordsToLatLngs(feature: Feature) {
  // @ts-ignore
  const { coordinates, type } = feature.geometry;
  // Check for point
  let depth = 0;
  if (type.startsWith("Multi")) depth = 1;
  return L.GeoJSON.coordsToLatLngs(coordinates, depth);
}

// --- Leaflet patch to fix blurry tiles on some browsers --- //
// Ensures it is only done once
export function applyTilePatchOnce() {
  if (window.__leafletTilePatchApplied) return;
  window.__leafletTilePatchApplied = true;

  if (!L?.GridLayer?.prototype) return;
  const proto = L.GridLayer.prototype as GridLayer & {
    _initTile: (tile: HTMLImageElement) => void;
  };
  const original = proto._initTile;
  L.GridLayer.include({
    _initTile(this: any, tile: HTMLImageElement) {
      original.call(this, tile);
      const tileSize = this.getTileSize();
      tile.style.width = `${tileSize.x + PX_CORRECTION}px`;
      tile.style.height = `${tileSize.y + PX_CORRECTION}px`;
    },
  });
}

// --- AUX functions --- //
function isBBox(value: unknown): value is BBox {
  return Array.isArray(value) && value.length >= 4 && value.every(isValidNumber);
}

function mergeBboxes(input: BBox | BBox[]): BBox | undefined {
  if (isBBox(input)) return [...input];

  if (!Array.isArray(input)) return undefined;

  const validBboxes = input.filter(isBBox);
  if (validBboxes.length === 0) return undefined;

  return validBboxes.reduce<BBox>(
    (acc, bb) => [
      Math.min(acc[0], bb[0]),
      Math.min(acc[1], bb[1]),
      Math.max(acc[2], bb[2]),
      Math.max(acc[3], bb[3]),
    ],
    [...validBboxes[0]],
  );
}

function normalizeBbox(bbox: BBox): BBox {
  return [
    Math.min(bbox[0], bbox[2]),
    Math.min(bbox[1], bbox[3]),
    Math.max(bbox[0], bbox[2]),
    Math.max(bbox[1], bbox[3]),
  ];
}
